/**
 * Food waste savings calculator.
 *
 * Reads the product price from a data attribute (rendered live by Liquid, never
 * hardcoded) and updates the yearly figure and the comparison line as the
 * slider moves. Framing stays comparative — it never claims a guaranteed saving.
 */

const TAG = 'savings-calculator';

class SavingsCalculator extends HTMLElement {
  connectedCallback() {
    if (!this.start()) requestAnimationFrame(() => this.start());
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.handleInput);
    this.cta?.removeEventListener('click', this.handleCta);
  }

  start() {
    if (this.started) return true;

    this.range = this.querySelector('[data-range]');
    if (!this.range) return false;

    this.valueOutput = this.querySelector('[data-value]');
    this.yearlyOutput = this.querySelector('[data-yearly]');
    this.compareOutput = this.querySelector('[data-compare]');
    this.cta = this.querySelector('[data-cta]');

    this.symbol = this.dataset.currencySymbol || '$';
    this.priceCents = Number(this.dataset.priceCents) || 0;
    this.priceFormatted = this.dataset.priceFormatted || '';

    this.range.addEventListener('input', this.handleInput);
    this.cta?.addEventListener('click', this.handleCta);

    this.started = true;
    this.render();
    return true;
  }

  handleInput = () => {
    this.render();
    document.dispatchEvent(
      new CustomEvent('freshness:calculator_interaction', {
        bubbles: true,
        detail: { weekly: this.weekly, yearly: this.weekly * 52 },
      })
    );
  };

  handleCta = () => {
    document.dispatchEvent(
      new CustomEvent('freshness:calculator_cta', {
        bubbles: true,
        detail: { weekly: this.weekly, yearly: this.weekly * 52 },
      })
    );
  };

  get weekly() {
    return Number(this.range?.value) || 0;
  }

  format(amount) {
    return `${this.symbol}${Math.round(amount).toLocaleString()}`;
  }

  render() {
    const weekly = this.weekly;
    const min = Number(this.range.min) || 0;
    const max = Number(this.range.max) || 100;

    this.style.setProperty('--sc-progress', `${((weekly - min) / (max - min)) * 100}%`);

    if (this.valueOutput) this.valueOutput.textContent = this.format(weekly);

    if (this.yearlyOutput) {
      this.yearlyOutput.textContent = (this.dataset.yearTemplate || "That's about {yearly} a year.")
        .replace('{yearly}', this.format(weekly * 52));
    }

    if (this.compareOutput && this.priceCents > 0 && weekly > 0) {
      const weeks = Math.max(1, Math.round(this.priceCents / 100 / weekly));
      this.compareOutput.textContent = (this.dataset.weeksTemplate || '')
        .replace('{weeks}', String(weeks))
        .replace('{price}', this.priceFormatted);
    }
  }
}

if (!customElements.get(TAG)) customElements.define(TAG, SavingsCalculator);
